import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import LandingSection from '../../../MyComponents/LandingSections/LandingSection';
import SectionEyebrow from '../../../MyComponents/LandingSections/SectionEyebrow';
import { START_FREE_LINK } from '../../../common/urlLinks';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../../constants';
import { BoardReadyIcon, DemonstrateIcon, PrepareIcon, ShareIcon } from './HeroMomentIcons';

const ASSETS = '/redesign/';

const MOMENTS = [
    { key: 'prepare', Icon: PrepareIcon },
    { key: 'boardReady', Icon: BoardReadyIcon },
    { key: 'demonstrate', Icon: DemonstrateIcon },
    { key: 'share', Icon: ShareIcon },
];

const TRUST_POINTS = ['noCard', 'browser', 'freePlan'];

// The board mock sheds its side rail first, then its top bar and second
// column - the same two steps the design source takes.
const NO_RAIL = '@media (max-width: 1000px)';
const ONE_COL = '@media (max-width: 700px)';

const useStyles = makeStyles(() => ({
    intro: {
        maxWidth: '900px',
        margin: '0 auto',
        textAlign: 'center',
    },
    eyebrowRow: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '22px',
    },
    title: {
        margin: '0 0 26px',
        fontSize: 'clamp(2rem, 4.4vw, 3.4rem)',
        lineHeight: 1.1,
        letterSpacing: '-0.035em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
        textWrap: 'balance',
    },
    ctaRow: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '12px',
    },
    cta: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        minHeight: '52px',
        padding: '0 26px',
        borderRadius: '999px',
        color: REDESIGN_COLORS.white,
        background: REDESIGN_COLORS.primary,
        fontSize: '1rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        '&:hover': {
            color: REDESIGN_COLORS.white,
            background: REDESIGN_COLORS.primaryHover,
        },
    },
    trust: {
        margin: '16px 0 0',
        color: REDESIGN_COLORS.muted,
        fontSize: '0.92rem',
        fontWeight: 600,
    },
    // A mock of the whiteboard itself: the app's real top bar and tool rail
    // framing the four moments of a lesson.
    board: {
        boxSizing: 'border-box',
        overflow: 'hidden',
        margin: '58px auto 0',
        border: `1px solid ${REDESIGN_COLORS.navBorder}`,
        borderRadius: '18px',
        background: REDESIGN_COLORS.white,
    },
    chrome: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        padding: '9px 14px',
        borderBottom: `1px solid ${REDESIGN_COLORS.boardDivider}`,
    },
    chromeImage: {
        display: 'block',
        height: 'clamp(26px, 3.4vw, 38px)',
        width: 'auto',
    },
    topbar: {
        [ONE_COL]: {
            display: 'none',
        },
    },
    body: {
        display: 'flex',
        alignItems: 'stretch',
    },
    steps: {
        flex: 1,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: '34px 44px',
        alignContent: 'center',
        margin: 0,
        padding: '56px 48px',
        listStyle: 'none',
        [NO_RAIL]: {
            gap: '34px 26px',
            padding: '34px 24px',
        },
        [ONE_COL]: {
            gridTemplateColumns: '1fr',
            padding: '28px 20px',
        },
    },
    rail: {
        flexShrink: 0,
        width: '74px',
        overflow: 'hidden',
        borderLeft: `1px solid ${REDESIGN_COLORS.boardDivider}`,
        [NO_RAIL]: {
            display: 'none',
        },
    },
    railImage: {
        display: 'block',
        width: '100%',
        height: 'auto',
    },
    iconWrap: {
        width: 'min(124px, 62%)',
        marginBottom: '14px',
    },
    icon: {
        display: 'block',
        width: '100%',
        height: 'auto',
        overflow: 'visible',
    },
    meta: {
        display: 'flex',
        alignItems: 'center',
        gap: '9px',
        marginBottom: '6px',
    },
    number: {
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        color: REDESIGN_COLORS.white,
        background: REDESIGN_COLORS.tealInk,
        fontFamily: REDESIGN_FONTS.accent,
        fontSize: '1.25rem',
        fontWeight: 700,
        lineHeight: 1,
    },
    time: {
        color: REDESIGN_COLORS.accent,
        fontFamily: REDESIGN_FONTS.accent,
        fontSize: 'clamp(1.15rem, 1.5vw, 1.45rem)',
        fontWeight: 700,
        lineHeight: 1,
        whiteSpace: 'nowrap',
    },
    stepTitle: {
        margin: '0 0 7px',
        fontSize: 'clamp(0.92rem, 1.12vw, 1.06rem)',
        lineHeight: 1.3,
        letterSpacing: '-0.02em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    stepText: {
        margin: 0,
        color: REDESIGN_COLORS.body,
        fontSize: 'clamp(0.8rem, 0.98vw, 0.9rem)',
        lineHeight: 1.5,
    },
}));

/**
 * The `#top` hero: a centred headline and CTA, then a mock of the board with
 * the four moments of a lesson laid out on its canvas.
 */
const HomeHero = () => {
    const classes = useStyles();
    const intl = useIntl();

    const t = (id) => intl.formatMessage({ id: `redesign.home.hero.${id}` });

    return (
        <LandingSection id="top" maxWidth={1080} paddingTop={46} paddingBottom={0}>
            <div className={classes.intro}>
                <div className={classes.eyebrowRow}>
                    <SectionEyebrow>
                        <FormattedMessage id="redesign.home.hero.eyebrow" />
                    </SectionEyebrow>
                </div>

                <h1 className={classes.title}>
                    <FormattedMessage id="redesign.home.hero.title" />
                </h1>

                <div className={classes.ctaRow}>
                    <Link to={START_FREE_LINK} className={classes.cta}>
                        <FormattedMessage id="redesign.home.hero.cta" />
                        <span aria-hidden="true">→</span>
                    </Link>
                </div>

                <p className={classes.trust}>{TRUST_POINTS.map((point) => t(`trust.${point}`)).join(' · ')}</p>
            </div>

            <div className={classes.board} role="group" aria-label={t('board.aria')}>
                <div className={classes.chrome}>
                    <img src={`${ASSETS}board-brand.png`} alt={t('board.brandAlt')} className={classes.chromeImage} />
                    <img
                        src={`${ASSETS}board-topbar.${intl.locale}.png`}
                        alt={t('board.topbarAlt')}
                        className={`${classes.chromeImage} ${classes.topbar}`}
                    />
                </div>

                <div className={classes.body}>
                    <ol className={classes.steps} aria-label={t('moments.aria')}>
                        {MOMENTS.map(({ key, Icon }, index) => (
                            <li key={key}>
                                <div className={classes.iconWrap}>
                                    <Icon className={classes.icon} />
                                </div>
                                <div className={classes.meta}>
                                    <span aria-hidden="true" className={classes.number}>
                                        {index + 1}
                                    </span>
                                    <span className={classes.time}>
                                        <FormattedMessage id={`redesign.home.hero.moment.${key}.time`} />
                                    </span>
                                </div>
                                <h3 className={classes.stepTitle}>
                                    <FormattedMessage id={`redesign.home.hero.moment.${key}.title`} />
                                </h3>
                                <p className={classes.stepText}>
                                    <FormattedMessage id={`redesign.home.hero.moment.${key}.text`} />
                                </p>
                            </li>
                        ))}
                    </ol>

                    <div className={classes.rail}>
                        <img
                            src={`${ASSETS}board-toolbar.${intl.locale}.png`}
                            alt={t('board.toolbarAlt')}
                            className={classes.railImage}
                        />
                    </div>
                </div>
            </div>
        </LandingSection>
    );
};

export default HomeHero;
